import {
  Interview_Reply_Event,
  useInterviewCurrentMessagesQuery,
  useInterviewReplySubscription,
} from '@generated/graphql';
import { Stack } from '@mui/material';
import { useToast } from '@myhrbox/myhrbox-frontend-core';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startQuestionTimer, stopQuestionTimer } from 'store/assessment/questionTimer';
import { getApolloErrorMessage } from 'utils/errorsParse';
import QuestionHeader from '../components/QuestionHeader';
import { QuestionPageContext } from '../context';
import Chat from './components/Chat';
import { ChatMessage } from './types';

interface InterviewQuestionProps {
  sessionId: string;
}

const InterviewQuestion = ({ sessionId }: InterviewQuestionProps) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[] | null>(null);
  const [interviewReplyLoading, setInterviewReplyLoading] = useState(false);
  const { showSuccessToast, showErrorToast } = useToast();

  const isInterviewFinished = messages?.length
    ? messages[messages.length - 1].event === Interview_Reply_Event.Finish
    : false;
  const messagesToPrint = isInterviewFinished ? messages?.slice(0, -1) : messages;

  const { handleFinishTesting, isLastQuestion } = useContext(QuestionPageContext);

  const { data: interviewReplySubscriptionData, error: interviewReplySubscriptionError } =
    useInterviewReplySubscription({
      variables: { sessionId },
      fetchPolicy: 'no-cache',
    });

  const { data: interviewCurrentMessagesData, loading: interviewCurrentMessagesLoading } =
    useInterviewCurrentMessagesQuery({
      variables: { sessionId },
      fetchPolicy: 'cache-and-network',
    });

  useEffect(() => {
    const { interviewCurrentMessages } = interviewCurrentMessagesData || {};

    if (interviewCurrentMessages) {
      setMessages(interviewCurrentMessages);
    }
  }, [interviewCurrentMessagesData]);

  useEffect(() => {
    const interviewReply = interviewReplySubscriptionData?.interviewReply;

    if (interviewReply) {
      setInterviewReplyLoading(false);
      setMessages((prev) => [...(prev || []), interviewReply]);
    }
  }, [interviewReplySubscriptionData]);

  useEffect(() => {
    if (interviewReplySubscriptionError) {
      setInterviewReplyLoading(false);
      showErrorToast(getApolloErrorMessage(interviewReplySubscriptionError));
    }
  }, [interviewReplySubscriptionError]);

  useEffect(() => {
    if (interviewReplyLoading) {
      stopQuestionTimer();
    } else {
      startQuestionTimer();
    }
  }, [interviewReplyLoading]);

  const loading = interviewCurrentMessagesLoading || interviewReplyLoading;

  useEffect(() => {
    if (isInterviewFinished) {
      showSuccessToast(t('successMessage'));
      handleFinishTesting();
    }
  }, [isInterviewFinished, isLastQuestion]);

  return (
    <Stack spacing={2}>
      <QuestionHeader title={t('assessment.question.interview.title')} />
      <Chat
        sessionId={sessionId}
        messages={messagesToPrint}
        loading={loading}
        setMessages={setMessages}
        setInterviewReplyLoading={setInterviewReplyLoading}
      />
    </Stack>
  );
};

export default InterviewQuestion;
